import {
    Box,
    VStack,
    Text,
    Input,
    FormControl,
    FormLabel,
    Select,
    Divider,
    Button,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore, useSelection, useEntities, useLayers, Entity } from '../../state';
import { useBIMStore } from '../../state/useBIMStore';
import { useSketchStore } from '../../state/useSketchStore';
import { BIMElement } from '../../core/types';
import { SketchSolid } from '../../core/sketch/types';
import { patchSceneObjectProps } from '../../core/scene/editRouter';
import { useContextMenuStore } from '../../state/useContextMenuStore';

type AppObject = Entity & { _source: 'app' };
// Normalize 'type' to string to match Entity's type property for the union, 
// though BIMElement already has 'type' (Family Type). 
// We might want to use 'category' as the main discriminator for logic, but for the union 'type' overlap is fine.
type BIMObject = BIMElement & { _source: 'bim' }; 
type SketchObject = SketchSolid & { _source: 'sketch' };

type SelectedObject = AppObject | BIMObject | SketchObject;

/**
 * Properties Panel Component — the body of the 2D/3D workbenches'
 * "Properties" side panel. Rendered inside the shared panel shell
 * (@suite/panels), which owns the panel chrome: dock edge, width, resize,
 * collapse, float and the tab header (registered as the 'properties' panel
 * in src/modules/cad/panels/registerCadPanels.tsx). This component renders
 * content only.
 *
 * Displays and edits properties of selected objects.
 */
export function PropertiesPanel() {
    const { t } = useTranslation();
    const selectedIds = useSelection();
    const entities = useEntities();
    const layers = useLayers();
    const updateEntity = useAppStore((s) => s.updateEntity);
    // Action-only reads: stable store refs, no subscription.
    const showContextMenu = useContextMenuStore((s) => s.show);
    // Narrow selectors instead of a whole-store subscribe: the panel used to
    // re-render on every unrelated BIM store write. `elements` only changes
    // identity when the element map is rebuilt by the kernel refresh.
    const elements = useBIMStore((s) => s.project.elements);
    const updateElement = useBIMStore((s) => s.updateElement);
    const solids = useSketchStore((s) => s.solids);
    const activeTool = useBIMStore((s) => s.activeTool);
    const toolProperties = useBIMStore((s) => s.toolProperties);
    const setToolProperty = useBIMStore((s) => s.setToolProperty);

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        showContextMenu(e.clientX, e.clientY, undefined, selectedIds);
    };

    const hasSelection = selectedIds.length > 0;
    const singleSelection = selectedIds.length === 1;

    // Resolve selected object from either AppStore entities, BIMStore elements
    // or the Sketch store (cross-workbench selection is source-agnostic, B4).
    const selectedObject = useMemo<SelectedObject | null>(() => {
        if (!hasSelection || !singleSelection) return null;
        const id = selectedIds[0];
        
        // Check App Entities first
        if (entities[id]) {
            return { ...entities[id], _source: 'app' } as AppObject;
        }
        
        // Check BIM Elements
        if (elements.has(id)) {
            const el = elements.get(id);
            if (el) {
                // Ensure type match if needed, but strict casting handles it
                return { ...el, _source: 'bim' } as BIMObject;
            }
        }

        // Check Sketch Solids
        const solid = solids[id];
        if (solid) {
            return { ...solid, _source: 'sketch' } as SketchObject;
        }
        
        return null;
    }, [selectedIds, entities, elements, solids, hasSelection, singleSelection]);

    const handleUpdate = (key: string, value: any) => {
        if (!selectedObject) return;

        if (selectedObject._source === 'app') {
            // Render-visible fields (visible/locked/layer/name) live on the
            // Entity itself; everything else belongs to the entity's data bag.
            if (key === 'visible' || key === 'locked' || key === 'layer' || key === 'name') {
                updateEntity(selectedObject.id, { [key]: value } as any);
            } else {
                updateEntity(selectedObject.id, {
                    data: { ...selectedObject.data, [key]: value }
                });
            }
        } else if (selectedObject._source === 'sketch') {
            // Protocol-level fields route through the cross-workbench edit
            // router, so the sketch store stays the single owner (B4).
            if (key === 'name' || key === 'visible' || key === 'locked') {
                patchSceneObjectProps(selectedObject.id, { [key]: value });
            }
        } else if (selectedObject._source === 'bim') {
            if (key === 'name') {
                 updateElement(selectedObject.id, { name: value });
            } else if (['width', 'height', 'thickness', 'baseOffset', 'topOffset'].includes(key)) {
                // Assume geometryData update
                const currentGeo = selectedObject.geometryData || {};
                updateElement(selectedObject.id, {
                    geometryData: { ...currentGeo, [key]: parseFloat(value) }
                });
            } else {
                 // Assume parameter update
                 const currentParams = selectedObject.parameters || {};
                 const updates: any = {
                     parameters: { ...currentParams, [key]: value }
                 };
                 // Model Text keeps its visible content in geometryData, and the
                 // 3D renderer prefers geometryData over parameters. Mirror text
                 // related edits into geometryData so the scene reflects them.
                 const geo = (selectedObject as any).geometryData;
                 const geoKeyMap: Record<string, string> = { Text: 'text', Size: 'size', Depth: 'depth' };
                 const geoKey = geoKeyMap[key];
                 if (geo && geoKey && geoKey in geo) {
                     updates.geometryData = {
                         ...geo,
                         [geoKey]: geoKey === 'text' ? value : (parseFloat(value) || geo[geoKey])
                     };
                 }
                 updateElement(selectedObject.id, updates);
            }
        }
    };

    const handleEditType = () => {
        if (!selectedObject || selectedObject._source !== 'bim') return;
        const typeString = selectedObject.type ?? '';
        const sep = typeString.indexOf(': ');
        const family = sep > 0 ? typeString.slice(0, sep) : typeString || 'Unnamed Family';
        const typeName = sep > 0 ? typeString.slice(sep + 2) : 'Default';
        useAppStore.getState().openTypeProperties({
            family,
            type: typeName,
            category: selectedObject.category,
            elementIds: [selectedObject.id],
        });
    };

    const renderProperty = (label: string, key: string, type: 'text' | 'number' | 'color' = 'text', readOnly = false) => {
        let value: any;
        if (selectedObject) {
            if (selectedObject._source === 'app') {
                 value = selectedObject.data[key];
            } else if (selectedObject._source === 'sketch') {
                 if (key === 'name') value = selectedObject.name;
            } else {
                 if (key === 'name') value = selectedObject.name;
                 else if (selectedObject.geometryData && key in selectedObject.geometryData) value = selectedObject.geometryData[key];
                 else if (selectedObject.parameters && key in selectedObject.parameters) value = selectedObject.parameters[key];
            }
        }

        if (type === 'number') {
            return (
                <FormControl key={key}>
                    <FormLabel fontSize="xs" color="gray.500">{label}</FormLabel>
                    <NumberInput
                        size="sm"
                        value={value as number || 0}
                        onChange={(_, val) => !readOnly && handleUpdate(key, val)}
                        variant="filled"
                        bg="surface.300"
                        isReadOnly={readOnly}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </FormControl>
            );
        }

        return (
            <FormControl key={key}>
                <FormLabel fontSize="xs" color="gray.500">{label}</FormLabel>
                <Input
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    value={value as string || ''}
                    onChange={(e) => !readOnly && handleUpdate(key, e.target.value)}
                    isReadOnly={readOnly}
                />
            </FormControl>
        );
    };

    const renderSpecificProperties = () => {
        if (!selectedObject) return null;

        if (selectedObject._source === 'sketch') {
            // Analytic solids keep their rich model in the sketch workbench;
            // the panel reports structure and routes edits via the router.
            return (
                <Text fontSize="xs" color="gray.500">
                    {t('ui.propsPanel.sketchSolidFaces', { faces: selectedObject.faces.length })}
                </Text>
            );
        }

        if (selectedObject._source === 'bim') {
            const category = selectedObject.category;
            
            if (category === 'Walls') {
                 return (
                     <>
                         {renderProperty('Base Offset', 'baseOffset', 'number')}
                         {renderProperty('Top Offset', 'topOffset', 'number')}
                         {renderProperty('Height', 'height', 'number', true)} 
                         {renderProperty('Thickness', 'thickness', 'number')}
                     </>
                 );
            }
            
            if (category === 'Doors' || category === 'Windows') {
                 return (
                     <>
                         {renderProperty('Width', 'width', 'number')}
                         {renderProperty('Height', 'height', 'number')}
                         {renderProperty('Sill Height', 'sillHeight', 'number')}
                     </>
                 );
            }
    
            if (category === 'Levels') {
                 return (
                     <>
                         {renderProperty('Elevation', 'elevation', 'number')}
                     </>
                 );
            }

            // Render dynamic parameters if any for generic BIM objects
            if (selectedObject.parameters) {
                 return Object.keys(selectedObject.parameters).map(key => (
                     renderProperty(key, key)
                 ));
            }
            return <Text fontSize="xs" color="gray.500">{t('ui.propsPanel.noSpecificProperties')}</Text>;
        }

        // App Object Specifics
        switch (selectedObject.type) {
            case 'circle':
                return renderProperty('Radius', 'radius', 'number');
            case 'box':
                return (
                    <>
                        {renderProperty('Width', 'width', 'number')}
                        {renderProperty('Height', 'height', 'number')}
                        {renderProperty('Depth', 'depth', 'number')}
                    </>
                );
            case 'text':
                return (
                    <>
                        {renderProperty('Content', 'text', 'text')}
                        {renderProperty('Size', 'fontSize', 'number')}
                    </>
                );
            default:
                return <Text fontSize="xs" color="gray.500">{t('ui.propsPanel.noSpecificProperties')}</Text>;
        }
    };

    const renderToolProperties = () => {
        if (activeTool === 'WALL') {
            return (
                <VStack spacing={3} align="stretch">
                    <Text fontSize="xs" fontWeight="semibold" color="gray.400">{t('ui.propsPanel.wallToolSettings')}</Text>
                    <FormControl>
                        <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.heightMm')}</FormLabel>
                        <NumberInput
                            size="sm"
                            value={toolProperties.wallHeight || 3000}
                            onChange={(_, val) => setToolProperty('wallHeight', val)}
                            variant="filled"
                            bg="surface.300"
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.baseOffsetMm')}</FormLabel>
                        <NumberInput
                            size="sm"
                            value={toolProperties.wallBaseOffset || 0}
                            onChange={(_, val) => setToolProperty('wallBaseOffset', val)}
                            variant="filled"
                            bg="surface.300"
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.locationLine')}</FormLabel>
                        <Select
                            size="sm"
                            variant="filled"
                            bg="surface.300"
                            value={toolProperties.wallLocationLine || 'WallCenterline'}
                            onChange={(e) => setToolProperty('wallLocationLine', e.target.value as any)}
                        >
                            <option value="WallCenterline">{t('ui.propsPanel.wallCenterline')}</option>
                            <option value="CoreCenterline">{t('ui.propsPanel.coreCenterline')}</option>
                            <option value="FinishFaceExterior">{t('ui.propsPanel.finishFaceExterior')}</option>
                            <option value="FinishFaceInterior">{t('ui.propsPanel.finishFaceInterior')}</option>
                            <option value="CoreFaceExterior">{t('ui.propsPanel.coreFaceExterior')}</option>
                            <option value="CoreFaceInterior">{t('ui.propsPanel.coreFaceInterior')}</option>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.joinType')}</FormLabel>
                        <Select
                            size="sm"
                            variant="filled"
                            bg="surface.300"
                            value={toolProperties.wallJoinType || 'Butt'}
                            onChange={(e) => setToolProperty('wallJoinType', e.target.value as any)}
                        >
                            <option value="Butt">{t('ui.propsPanel.butt')}</option>
                            <option value="Miter">{t('ui.propsPanel.miter')}</option>
                            <option value="SquareOff">{t('ui.propsPanel.squareOff')}</option>
                        </Select>
                    </FormControl>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel fontSize="xs" color="gray.500" mb="0">{t('ui.propsPanel.chain')}</FormLabel>
                        <Select
                            size="sm"
                            variant="filled"
                            bg="surface.300"
                            value={toolProperties.wallChain ? 'yes' : 'no'}
                            onChange={(e) => setToolProperty('wallChain', e.target.value === 'yes')}
                        >
                            <option value="yes">{t('ui.propsPanel.yes')}</option>
                            <option value="no">{t('ui.propsPanel.no')}</option>
                        </Select>
                    </FormControl>
                </VStack>
            );
        }
        return (
            <VStack spacing={4} py={4}>
                <Text fontSize="sm" color="gray.500" textAlign="center">{t('ui.propsPanel.noSelection')}</Text>
                <Text fontSize="xs" color="gray.600" textAlign="center">{t('ui.propsPanel.selectAnObjectToViewIts')}</Text>
            </VStack>
        );
    };

    return (
        <Box onContextMenu={handleContextMenu}>
            {/* Content */}
            {!hasSelection ? (
                    renderToolProperties()
                ) : !singleSelection ? (
                    <VStack spacing={2}>
                        <Text fontSize="sm" color="gray.400">
                            {selectedIds.length} objects selected
                        </Text>
                        <Divider />
                        <FormControl>
                            <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.visibility')}</FormLabel>
                            <Select
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                placeholder={t('ui.propsPanel.setVisibility')}
                                onChange={(e) => {
                                    const visible = e.target.value === 'visible';
                                    selectedIds.forEach(id => {
                                        // Router keeps every source's visible
                                        // flag on its living copy; sources that
                                        // don't support it (BIM view scope) no-op.
                                        patchSceneObjectProps(id, { visible });
                                    });
                                }}
                            >
                                <option value="visible">{t('ui.propsPanel.allVisible')}</option>
                                <option value="hidden">{t('ui.propsPanel.allHidden')}</option>
                            </Select>
                        </FormControl>
                    </VStack>
                ) : (
                    <VStack spacing={3} align="stretch">
                        {/* Object Info */}
                        <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>{t('ui.propsPanel.type')}</Text>
                            <Text fontSize="sm" color="gray.200" fontWeight="medium">
                                {selectedObject?._source === 'sketch'
                                    ? t('ui.propsPanel.sketchSolid')
                                    : selectedObject?._source === 'app'
                                        ? selectedObject?.type.toUpperCase()
                                        : (selectedObject?.type ?? '').toUpperCase()}
                            </Text>
                            {selectedObject?._source === 'bim' && (
                                <Button
                                    size="xs"
                                    mt={2}
                                    w="full"
                                    variant="outline"
                                    borderColor="gray.600"
                                    color="gray.200"
                                    onClick={handleEditType}
                                    title={t('ui.propsPanel.editTheParametersOfThisFamily')}
                                >{t('ui.propsPanel.editType')}</Button>
                            )}
                        </Box>

                        <Divider />

                        {/* Identity */}
                        <VStack spacing={2} align="stretch">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.400">{t('ui.propsPanel.identity')}</Text>
                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.name')}</FormLabel>
                                <Input
                                    size="sm"
                                    variant="filled"
                                    bg="surface.300"
                                    value={selectedObject?.name || ''}
                                    onChange={(e) => selectedObject && handleUpdate('name', e.target.value)}
                                />
                            </FormControl>
                            {selectedObject?._source === 'app' && (
                                <FormControl>
                                    <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.layer')}</FormLabel>
                                    <Select
                                        size="sm"
                                        variant="filled"
                                        bg="surface.300"
                                        value={selectedObject.layer}
                                        onChange={(e) => handleUpdate('layer', e.target.value)}
                                    >
                                        {Object.values(layers).map(layer => (
                                            <option key={layer.id} value={layer.id}>
                                                {layer.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </VStack>

                        <Divider />

                        {/* Specific Properties */}
                        <VStack spacing={2} align="stretch">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.400">{t('ui.propsPanel.parameters')}</Text>
                            {renderSpecificProperties()}
                        </VStack>

                        <Divider />

                        {/* Display (App entities + sketch solids carry their
                            own visible/locked flags on the living copy) */}
                        {(selectedObject?._source === 'app' || selectedObject?._source === 'sketch') && (
                            <VStack spacing={2} align="stretch">
                                <Text fontSize="xs" fontWeight="semibold" color="gray.400">{t('ui.propsPanel.display')}</Text>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.visible')}</FormLabel>
                                    <Select
                                        size="sm"
                                        variant="filled"
                                        bg="surface.300"
                                        value={selectedObject.visible ? 'yes' : 'no'}
                                        onChange={(e) => handleUpdate('visible', e.target.value === 'yes')}
                                    >
                                        <option value="yes">{t('ui.propsPanel.yes')}</option>
                                        <option value="no">{t('ui.propsPanel.no')}</option>
                                    </Select>
                                </FormControl>
                                {selectedObject._source === 'sketch' && (
                                    <FormControl>
                                        <FormLabel fontSize="xs" color="gray.500">{t('ui.propsPanel.locked')}</FormLabel>
                                        <Select
                                            size="sm"
                                            variant="filled"
                                            bg="surface.300"
                                            value={selectedObject.locked ? 'yes' : 'no'}
                                            onChange={(e) => handleUpdate('locked', e.target.value === 'yes')}
                                        >
                                            <option value="yes">{t('ui.propsPanel.yes')}</option>
                                            <option value="no">{t('ui.propsPanel.no')}</option>
                                        </Select>
                                    </FormControl>
                                )}
                            </VStack>
                        )}
                    </VStack>
                )}
        </Box>
    );
}

